import {
  ref,
  computed,
  inject,
  provide,
  type Ref,
  watch,
  type EmitFn,
  type ComputedRef,
  nextTick,
  type ComponentPublicInstance,
} from 'vue'

import { cloneDeep, keyBy } from 'lodash-es'
import type useConfirmation from '@lib/composables/useConfirmation'
import useLegacyCollectionFileLoader from '@lib/composables/useLegacyCollectionFileLoader'
import type {
  CollectionGroup,
  CollectionPlayer,
  CollectionPlayerV2,
  CollectionSlide,
  CollectionSlideV2,
} from '@collection-player/types/collection-player.type'
import type { FetchFileList, SelectFileContent } from '@collection-selector/types/common.type'
import {
  generateNewCollection,
  generateNewEmptyGroup,
  getDefaultGroupName,
  getDefaultNewGroupName,
} from '@collection-selector/utils/data.util'
import {
  parseCollectionPlayerSlidesToContentSelector,
  parseContentSelectorToCollectionPlayerSlides,
  parseFileToCollectionPlayer,
} from '@collection-selector/utils/content-selector-adapter.util'
import type { FileRetrieve } from '@openapi'
import { FileContentTypeEnum } from '@openapi'
import type { Translations } from '@collection-selector/types/collection-selector.type'
import type { SelectedFile, SelectedPage } from '@content-selector/stores/app'
import type { Emit } from '@collection-selector/App.vue'
import { useContentWarning } from '@collection-selector/composables/content-warning.use'

type StorePayload = {
  initialCollection?: CollectionPlayer
  t: (key: string, values?: Record<string, string | number>) => string
  confirmation: ReturnType<typeof useConfirmation>
  selectFileContent: SelectFileContent
  attachTo?: HTMLElement | string
  openSettings?: () => void
  closeOnSettings?: boolean
  fetchFile: (id: string) => Promise<FileRetrieve>
  fetchFileList: FetchFileList
  zIndex?: number
  emit: EmitFn<Emit>
  translations?: Translations
  contentRef: Ref<ComponentPublicInstance | null>
}

type StoreData = {
  collection: Ref<CollectionPlayer>
  expandedGroups: Ref<string[]>
  usedFiles: Ref<Record<FileRetrieve['id'], FileRetrieve>>
  addGroup: (group?: Partial<CollectionGroup>) => CollectionGroup | undefined
  deleteGroup: (groupId: string, groupName: string) => void
  updateGroup: (group: Partial<Omit<CollectionGroup, 'id'>> & Pick<CollectionGroup, 'id'>) => void
  updateGroups: (groups: CollectionGroup[]) => void
  updateSlides: (groupId: string, slides: CollectionSlide[]) => void
  updateCollectionName: (name: string) => void
  updateGroupName: (groupId: string, name: string) => void
  deleteSlide: (groupId: string, slide: CollectionSlide) => void
  addSlides: (groupId: string, filters?: Record<string, any>) => Promise<CollectionSlide[] | undefined>
  moveSlideToGroup: (fromGroup: string, toGroup: string, slide: CollectionSlide) => void
  selectFileContent: SelectFileContent
  openSettings: () => void
  closeOnSettings?: boolean
  fetchFile: (id: string) => Promise<FileRetrieve>
  fetchFileList: FetchFileList
  childModalZIndex?: number
  cancel: () => void
  saveAndClose: () => void
  translations?: Translations
  isSettingsButtonVisible: ComputedRef<boolean>
  contentWarning: ReturnType<typeof useContentWarning>
  isLoadingFiles: ComputedRef<boolean>
  areRequiredFilesLoaded: ComputedRef<boolean>
}

const StoreKey = Symbol('CollectionSelectorStore')

export function provideAppStore({
  initialCollection,
  t,
  confirmation,
  selectFileContent,
  attachTo,
  openSettings: openSettingsFn,
  closeOnSettings,
  fetchFile,
  fetchFileList,
  zIndex,
  emit,
  translations,
  contentRef,
}: StorePayload) {
  const rawCollection = ref<CollectionPlayer>({ ...generateNewCollection(t), ...(cloneDeep(initialCollection) ?? {}) })

  // Use legacy collection file loader for backward compatibility with old format collections
  const fileLoader = useLegacyCollectionFileLoader()

  const childModalZIndex = zIndex !== undefined ? zIndex + 1 : undefined
  const isSettingsButtonVisible = computed(() => !!openSettingsFn || !!closeOnSettings)

  // Track if we're currently loading files
  const isLoadingFiles = ref(false)

  // Check if all required files are loaded for display
  const areRequiredFilesLoaded = computed(() => {
    const rawData = rawCollection.value

    // If no groups or empty, no files to load
    if (!rawData.groups || rawData.groups.length === 0) {
      return true
    }

    // Check if this is V2 format (has file_id instead of file objects)
    const isV2Format = rawData.groups.every(
      (group: any) =>
        Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide && !slide.file),
    )

    if (!isV2Format) {
      return true // Already V1 format, no loading needed
    }

    // Get all file IDs that need to be loaded
    const allFileIds = rawData.groups.flatMap((group: any) =>
      group.slides.map((slide: any) => slide.file_id).filter(Boolean),
    )

    // Check if all files are resolved (either loaded successfully or failed with error)
    // This prevents infinite spinner when files return 404
    return allFileIds.every((fileId) => fileLoader.isFileResolved(fileId))
  })

  // Transform V2 data with file_id to V1 format with loaded file objects
  const collection = computed<CollectionPlayer>(() => {
    const rawData = rawCollection.value

    // If no groups or empty, return as-is
    if (!rawData.groups || rawData.groups.length === 0) {
      return rawData
    }

    // Check if this is V2 format (has file_id instead of file objects)
    const isV2Format = rawData.groups.every(
      (group: any) =>
        Array.isArray(group?.slides) && group.slides.every((slide: any) => 'file_id' in slide && !slide.file),
    )

    if (!isV2Format) {
      return rawData // Already V1 format
    }

    // Only transform if all files are loaded, otherwise return empty structure
    if (!areRequiredFilesLoaded.value) {
      return {
        ...rawData,
        groups: rawData.groups.map((group: any) => ({
          id: group.id,
          name: group.name,
          slides: [], // Empty slides until all files are loaded
        })),
      }
    }

    // Transform V2 back to V1 format using loaded file data
    const transformedGroups = rawData.groups.map((group: any) => ({
      id: group.id,
      name: group.name,
      slides: group.slides
        .map((slide: any) => {
          const loadedFile = fileLoader.getFile(slide.file_id)

          // Skip slides where the file failed to load or isn't available
          if (!loadedFile) {
            console.warn(`File ${slide.file_id} failed to load or is not available`)
            return null
          }

          // Transform File to CollectionFileEntity-like structure
          const collectionFile = {
            id: loadedFile.id,
            name: loadedFile.name,
            type: loadedFile.type,
            tags: loadedFile.tags,
            metadata: loadedFile.metadata,
            thumbnail_url: loadedFile.thumbnail_url,
            permissions: loadedFile.permissions,
            content_url: loadedFile.content_url,
            content_type: loadedFile.content_type,
            pspdfkit_auth_payload: loadedFile.pspdfkit_auth_payload,
            pspdfkit_document_id: loadedFile.pspdfkit_document_id,
            pspdfkit_server_url: loadedFile.pspdfkit_server_url,
          }

          if (slide.index !== undefined) {
            // This is a slide (page from PDF)
            const slideUrl = loadedFile.content_thumbnails?.[slide.index] || loadedFile.thumbnail_url || null

            return {
              id: slide.id,
              type: 'slide' as const,
              file: collectionFile,
              slide: {
                index: slide.index,
                url: slideUrl,
              },
            }
          } else {
            // This is a regular file
            return {
              id: slide.id,
              type: 'file' as const,
              file: collectionFile,
            }
          }
        })
        .filter(Boolean), // Remove any null entries
    }))

    return {
      ...rawData,
      groups: transformedGroups,
    }
  })

  const expandedGroups = ref<string[]>(rawCollection.value.groups.map((group) => group.id))

  const setCollection = (newCollection: CollectionPlayer) => {
    rawCollection.value = newCollection
  }

  const updateGroup = (group: Partial<Omit<CollectionGroup, 'id'>> & Pick<CollectionGroup, 'id'>) => {
    setCollection({
      ...rawCollection.value,
      groups: [
        ...rawCollection.value.groups.map((_group) =>
          _group.id === group.id
            ? {
                ..._group,
                ...group,
              }
            : _group,
        ),
      ],
    })
  }

  const createGroup = (group?: Partial<CollectionGroup>) => ({ ...generateNewEmptyGroup(t), ...(group ?? {}) })

  const groupSlidesByFileSequence = (slides: CollectionSlide[]): CollectionSlide[][] => {
    if (slides.length === 0) return []

    const groups: CollectionSlide[][] = []
    let currentGroup: CollectionSlide[] = [slides[0]]

    for (let i = 1; i < slides.length; i++) {
      const currentSlide = slides[i]
      const previousSlide = slides[i - 1]

      if (currentSlide.file.id === previousSlide.file.id) {
        currentGroup.push(currentSlide)
      } else {
        groups.push(currentGroup)
        currentGroup = [currentSlide]
      }
    }

    groups.push(currentGroup)

    return groups
  }

  const addGroup = (group?: Partial<CollectionGroup>): CollectionGroup | undefined => {
    const newGroup = createGroup(group)

    setCollection({
      ...rawCollection.value,
      groups: [...rawCollection.value.groups, newGroup],
    })

    expandGroup(newGroup.id)

    nextTick(() => {
      const draggableElement = contentRef.value?.$el
      const scrollContainer = draggableElement?.parentElement

      if (!scrollContainer) return

      scrollContainer.scrollTop = scrollContainer.scrollHeight
    })

    return newGroup
  }

  const deleteGroup = (groupId: string, groupName: string) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      hasExit: false,
      title: t('canvasUI.collectionSelector.deleteItemModal.title', { name: groupName }),
      content: t('canvasUI.collectionSelector.deleteItemModal.content', { name: groupName }),
      confirmText: t('canvasUI.collectionSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.collectionSelector.deleteItemModal.cancel'),
      onConfirm: () => {
        const groups = rawCollection.value.groups.filter((group) => group.id !== groupId)

        if (groups.length === 0) {
          const newGroup = createGroup()

          groups.push(newGroup)
          expandGroup(newGroup.id)
        }

        setCollection({
          ...rawCollection.value,
          groups,
        })
      },
    })
  }

  const updateGroups = (groups: CollectionGroup[]) => {
    setCollection({
      ...rawCollection.value,
      groups,
    })
  }

  const updateSlides = (groupId: string, slides: CollectionSlide[]) => {
    setCollection({
      ...rawCollection.value,
      groups: rawCollection.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              slides,
            }
          : group,
      ),
    })
  }

  const updateCollectionName = (name: string) => {
    const trimmedName = name.trim()

    setCollection({
      ...rawCollection.value,
      name: trimmedName || t('canvasUI.collectionSelector.defaultCollectionName'),
    })
  }

  const updateGroupName = (groupId: string, name: string) => {
    const trimmedName = name.trim()

    setCollection({
      ...rawCollection.value,
      groups: rawCollection.value.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              name: trimmedName || getDefaultGroupName(t),
            }
          : group,
      ),
    })
  }

  const deleteSlide = (groupId: string, slide: CollectionSlide) => {
    confirmation.danger({
      zIndex: childModalZIndex,
      title: t('canvasUI.collectionSelector.deleteItemModal.title', {
        name: slide.file?.name ?? '',
      }),
      content: t('canvasUI.collectionSelector.deleteItemModal.content', {
        name: slide.file?.name ?? '',
      }),
      confirmText: t('canvasUI.collectionSelector.deleteItemModal.confirm'),
      cancelText: t('canvasUI.collectionSelector.deleteItemModal.cancel'),
      hasExit: false,
      onConfirm: async () => {
        const groups = rawCollection.value.groups.map((group) => {
          if (group.id === groupId) {
            const slides = group.slides.filter((i) => i.id !== slide.id)

            return {
              ...group,
              slides,
            }
          }

          return group
        })

        updateGroups(groups)
      },
    })
  }

  const findGroup = (groupId: string) => rawCollection.value.groups.find((group) => group.id === groupId)

  async function fetchFilesById(items: (SelectedFile | SelectedPage)[]) {
    try {
      const fileIds = getUniqueIdsFromItems(items)
      const files = await Promise.all(fileIds.map(fetchFile))
      return keyBy(files, 'id')
    } catch (e) {
      console.error('[App.vue]: failed to fetch files', e)
      return null
    }
  }

  const getUniqueIdsFromItems = (items: (SelectedFile | SelectedPage)[]) => {
    const ids = new Set<string>()

    items.forEach((item) => {
      if (item.type === 'file') {
        ids.add(item.id)
      } else if (item.type === 'page') {
        ids.add(item.file.id)
      }
    })

    return Array.from(ids)
  }

  const getUsedFileIds = () =>
    rawCollection.value.groups.reduce((acc, group) => {
      group.slides.forEach((slide) => {
        // Handle both V1 (slide.file.id) and V2 (slide.file_id) formats
        const id = slide.file?.id || (slide as any).file_id

        if (id && !acc.includes(id)) {
          acc.push(id)
        }
      })

      return acc
    }, [] as string[])

  const loadMissingUsedFiles = async () => {
    const allFileIds = getUsedFileIds()

    if (allFileIds.length === 0) return

    // Set loading state
    isLoadingFiles.value = true

    try {
      // Initialize files in the loader
      fileLoader.initializeFiles(allFileIds)

      // Load all files immediately
      await fileLoader.loadAllFiles(allFileIds)
    } finally {
      // Clear loading state
      isLoadingFiles.value = false
    }
  }

  const addSlides = async (groupId: string, filters?: Record<string, any>) => {
    const group = findGroup(groupId)

    if (!group) {
      return
    }

    const data = await selectFileContent({
      attach_to: attachTo,
      selections: parseCollectionPlayerSlidesToContentSelector(group?.slides),
      allowed_selector_types: ['content'],
      allowed_content_types: [
        FileContentTypeEnum.PDF,
        FileContentTypeEnum.VIDEO,
        FileContentTypeEnum.WEB,
        FileContentTypeEnum.IMAGE,
      ],
      initial_metadata_filters: filters,
    })

    if (!data || data.user_action === 'cancelled' || !data.content) {
      return
    }

    const filesById = await fetchFilesById(data.content)

    if (!filesById) return

    const slides = parseContentSelectorToCollectionPlayerSlides({ selectedItems: data.content, filesById })

    const isEmptyCollection =
      rawCollection.value.groups.length === 1 &&
      rawCollection.value.groups[0].slides.length === 0 &&
      rawCollection.value.groups[0].name === getDefaultNewGroupName(t)

    if (isEmptyCollection && slides.length > 0) {
      if (data.content.length === 1) {
        const fileId = slides[0].file.id
        const fileDetails = filesById[fileId]

        if (fileDetails) {
          if (
            fileDetails.content_type === 'pdf' &&
            fileDetails.original_extension === 'pptx' &&
            fileDetails.metadata?.pptx_data?.sections?.length
          ) {
            const parsedCollection = parseFileToCollectionPlayer(t, fileDetails)

            if (parsedCollection && parsedCollection.groups.length > 0) {
              // For empty collection, replace entirely with parsed collection
              setCollection(parsedCollection)

              if (parsedCollection.groups[0]) {
                expandGroup(parsedCollection.groups[0].id)
              }

              contentWarning.validateCollectionSlides(parsedCollection.groups.flatMap((g: CollectionGroup) => g.slides))

              return parsedCollection.groups.flatMap((g: CollectionGroup) => g.slides)
            }
          }
        }
      } else {
        const groupedSlides = groupSlidesByFileSequence(slides)

        const newGroups = groupedSlides.map((slideGroup) => {
          const fileName = slideGroup[0].file.name

          return createGroup({
            name: fileName ?? getDefaultNewGroupName(t),
            slides: slideGroup,
          })
        })

        // For empty collection with multiple files, replace entirely with new grouped structure
        setCollection({
          ...rawCollection.value,
          groups: newGroups,
        })

        newGroups.forEach((group) => expandGroup(group.id))

        const allSlides = newGroups.flatMap((group) => group.slides)
        contentWarning.validateCollectionSlides(allSlides)

        return allSlides
      }
    }

    // Convert V1 slides to V2 format to match rawCollection format
    const slidesV2 = slides
      .map((slide) => ({
        id: slide.id,
        file_id: slide.file?.id || '',
        index: slide.type === 'slide' ? slide.slide?.index : undefined,
      }))
      .filter((slide) => slide.file_id && slide.file_id.trim() !== '')

    updateGroup({
      id: groupId,
      slides: slidesV2 as any,
    })

    if (group.slides.length === 0 && group.name === getDefaultNewGroupName(t) && slides[0]?.file?.name) {
      updateGroupName(groupId, slides[0].file?.name || '')
    }

    expandGroup(groupId)
    contentWarning.validateCollectionSlides(slides)

    return slides
  }

  const expandGroup = (groupId: string) => {
    if (!expandedGroups.value.includes(groupId)) {
      expandedGroups.value.push(groupId)
    }
  }

  const moveSlideToGroup = (fromGroup: string, toGroup: string, slide: CollectionSlide) => {
    // Convert slide to V2 format to match rawCollection format
    const slideV2: CollectionSlideV2 = {
      id: slide.id,
      file_id: slide.file?.id || '',
      index: slide.type === 'slide' ? slide.slide?.index : undefined,
    }

    updateGroups(
      rawCollection.value.groups.map((group) => {
        if (group.id === fromGroup) {
          return {
            ...group,
            slides: group.slides.filter((s) => s.id !== slide.id),
          }
        }

        if (group.id === toGroup) {
          return {
            ...group,
            slides: [...group.slides, slideV2 as any],
          }
        }

        return group
      }),
    )
  }

  const contentWarning = useContentWarning()

  const cancel = () => {
    emit('cancel')
  }

  // Convert V1 collection data back to V2 format for saving
  // Also handles data that's already in V2 format
  const convertToV2Format = (v1Data: CollectionPlayer): CollectionPlayerV2 => {
    const v2Groups = v1Data.groups.map((group) => ({
      id: group.id,
      name: group.name,
      slides: group.slides
        .map((slide): CollectionSlideV2 => {
          // Check if slide is already in V2 format (has file_id directly)
          const slideAsV2 = slide as any
          if ('file_id' in slideAsV2 && slideAsV2.file_id) {
            // Already V2 format, return as-is
            return {
              id: slideAsV2.id,
              file_id: slideAsV2.file_id,
              index: slideAsV2.index,
            }
          }

          // V1 format, convert to V2
          return {
            id: slide.id,
            file_id: slide.file?.id || '',
            index: slide.type === 'slide' ? slide.slide?.index : undefined,
          }
        })
        .filter((slide) => slide.file_id && slide.file_id.trim() !== ''), // Filter out slides with empty or invalid file_id
    }))

    return {
      ...v1Data,
      groups: v2Groups,
    }
  }

  const saveAndClose = () => {
    // Filter out slides with failed files before saving
    const cleanedCollection = {
      ...rawCollection.value,
      groups: rawCollection.value.groups.map((group: any) => ({
        ...group,
        slides: group.slides.filter((slide: any) => {
          const fileId = slide.file_id || slide.file?.id
          if (!fileId) return false
          // Keep only slides where the file loaded successfully (not failed)
          return !fileLoader.hasFileFailed(fileId)
        }),
      })),
    }

    const v2Data = convertToV2Format(cleanedCollection as any)
    emit('close', v2Data)
  }

  const openSettings = () => {
    if (closeOnSettings) {
      // Filter out slides with failed files before emitting settings
      const cleanedCollection = {
        ...rawCollection.value,
        groups: rawCollection.value.groups.map((group: any) => ({
          ...group,
          slides: group.slides.filter((slide: any) => {
            const fileId = slide.file_id || slide.file?.id
            if (!fileId) return false
            // Keep only slides where the file loaded successfully (not failed)
            return !fileLoader.hasFileFailed(fileId)
          }),
        })),
      }

      const v2Data = convertToV2Format(cleanedCollection as any)
      emit('settings', v2Data)
    }

    openSettingsFn?.()
  }

  const storeData: StoreData = {
    collection,
    expandedGroups,
    selectFileContent,
    openSettings,
    fetchFile,
    fetchFileList,
    usedFiles: fileLoader.filesById, // Expose loaded files through the same interface
    childModalZIndex,
    closeOnSettings,
    addGroup,
    deleteGroup,
    updateGroup,
    updateGroups,
    updateSlides,
    updateCollectionName,
    updateGroupName,
    deleteSlide,
    addSlides,
    moveSlideToGroup,
    cancel,
    saveAndClose,
    translations,
    isSettingsButtonVisible,
    contentWarning,
    isLoadingFiles: computed(() => isLoadingFiles.value),
    areRequiredFilesLoaded,
  }

  provide<StoreData>(StoreKey, storeData)

  // Create a stable reference for file loading that doesn't change unnecessarily
  const dataForFileLoading = computed(() => {
    if (!rawCollection.value.groups) return null

    // Only trigger file loading when the actual file IDs change
    const fileIds = rawCollection.value.groups.flatMap((group) =>
      group.slides.map((slide) => slide.file?.id || (slide as any).file_id).filter(Boolean),
    )

    return {
      fileIds,
    }
  })

  watch(
    dataForFileLoading,
    (newData) => {
      if (newData?.fileIds.length) {
        loadMissingUsedFiles()
      }
    },
    { deep: true, immediate: true },
  )

  return storeData
}

export function useAppStore() {
  const storeData = inject<StoreData>(StoreKey)

  if (!storeData) {
    throw new Error('useAppStore() is called without provider.')
  }

  return storeData
}
